/**
 * 業務を追加する（統合版）。
 *
 * 「通常業務」と「臨時業務」の区別を無くしたので、追加の入口もこれ1つにする。
 * 画面では作業内容と時刻だけを選び、どこに記録するか（勤怠として持つか、
 * 作業の記録として持つか）はシステム側で振り分ける。
 *
 * 労働区分（機能フラグ labor_categories）: 作業内容は区分ごとに並べる。
 *   ・通常の作業（労働時間として数える）
 *   ・緊急・安全対応（非常作業68条＝上限計算から除外／安全臨時労働64条＝上限超えが合法）… 事由が必須
 *   ・労働時間外の記録（食事・共食／自発的作業／私的作業／禁止された作業）… 労働時間に含めない
 * 通常以外の区分は必ず「作業の記録」として保存する（出退勤の打刻として記録すると労働時間になってしまうため）。
 */
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../services/api';
import { useVesselWorkTypes } from '../hooks/useVesselWorkTypeOptions';
import { hasFeature } from '../utils/featureFlags';

interface Props {
  date: string;                 // YYYY-MM-DD
  vesselId: string;             // 記録する船
  onClose: () => void;
  onSaved: () => void;
}

/** サーバーが返す作業種別（区分の扱いつき） */
export interface ActivityTypeWithCategory {
  id: string; name: string; emoji: string; color?: string;
  labor_category?: string | null; labor_category_name?: string | null; labor_category_color?: string | null;
  counts_as_work?: number; counts_toward_limit?: number; permits_over_limit?: number;
  requires_reason?: number; counts_as_rest?: number; notify_shore?: number;
}

/** 区分の扱いから3本のどれに入るかを決める */
export function bucketOf(t: Pick<ActivityTypeWithCategory, 'counts_as_work' | 'counts_toward_limit' | 'permits_over_limit'> | undefined): 'work' | 'exempt' | 'nonwork' {
  if (!t) return 'work';
  if (t.counts_as_work === 0) return 'nonwork';
  if (t.counts_toward_limit === 0 || t.permits_over_limit === 1) return 'exempt';
  return 'work';
}
export const BUCKET_LABEL: Record<'work' | 'exempt' | 'nonwork', { title: string; color: string; note: string }> = {
  work:    { title: '作業',           color: '#0057d9', note: '労働時間として数えます' },
  exempt:  { title: '緊急・安全対応', color: '#dc2626', note: '労働時間の上限とは別枠で記録します（事由が必要）' },
  nonwork: { title: '労働時間外の記録', color: '#64748b', note: '労働時間には含めません（食事・自発的な作業など）' },
};
/** 事由の定型（緊急・安全対応で選べる） */
export const REASON_PRESETS = ['荒天による避泊・操船', '人命救助（他船・海中転落）', '機関故障の応急対応', '火災・浸水などの非常事態', '入出港時の安全確保', 'その他（下に記入）'];

interface Choice { key: string; name: string; emoji: string; kind: 'session' | 'activity'; masterId?: string; type?: ActivityTypeWithCategory; bucket: 'work' | 'exempt' | 'nonwork' }

export const AddWorkModal: React.FC<Props> = ({ date, vesselId, onClose, onSaved }) => {
  const laborOn = hasFeature('labor_categories');
  const workTypes = useVesselWorkTypes(vesselId);
  const [activityTypes, setActivityTypes] = useState<ActivityTypeWithCategory[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('12:00');
  const [note, setNote] = useState('');
  const [reasonPreset, setReasonPreset] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!vesselId) return;
    apiFetch<{ data?: ActivityTypeWithCategory[] }>(`/vessels/${vesselId}/activity-types`)
      .then(r => setActivityTypes(r?.data ?? []))
      .catch(() => setActivityTypes([]));
  }, [vesselId]);

  // 作業内容の候補は1つのリストにまとめる（画面上は区別しない）。
  // 業務種別マスタと特別作業マスタは同じ内容を持ち合う運用なので、そのまま連結すると
  // 「航海当直」などが2回並んでしまう。名前で1本化する。
  //
  // どちらを残すかで記録の中身が変わる（session=勤務セッション / activity=作業ログ）ため、
  // これまで一覧の先に出ていた業務種別（session）を優先し、記録のされ方を変えない。
  // ただし労働区分が「通常」以外の作業（食事・非常作業…）は必ず作業ログとして記録する。
  const choices = useMemo<Choice[]>(() => {
    const byName = new Map<string, Choice>();
    const norm = (n: string) => n.replace(/\s+/g, '').trim();
    const actByName = new Map<string, ActivityTypeWithCategory>();
    for (const a of activityTypes) actByName.set(norm(a.name), a);
    for (const w of workTypes) {
      const k = norm(w.name);
      if (!k || byName.has(k)) continue;
      const a = actByName.get(k);
      const bucket = laborOn ? bucketOf(a) : 'work';
      if (a && bucket !== 'work') {
        byName.set(k, { key: `a:${a.id}`, name: a.name, emoji: a.emoji || w.emoji, kind: 'activity', masterId: a.id, type: a, bucket });
      } else {
        byName.set(k, { key: `w:${w.name}`, name: w.name, emoji: w.emoji, kind: 'session', type: a, bucket: 'work' });
      }
    }
    for (const a of activityTypes) {
      const k = norm(a.name);
      if (!k || byName.has(k)) continue;
      byName.set(k, { key: `a:${a.id}`, name: a.name, emoji: a.emoji, kind: 'activity', masterId: a.id, type: a, bucket: laborOn ? bucketOf(a) : 'work' });
    }
    return [...byName.values()];
  }, [workTypes, activityTypes, laborOn]);

  const groups = useMemo(() => {
    const order: Array<'work' | 'exempt' | 'nonwork'> = ['work', 'exempt', 'nonwork'];
    return order.map(b => ({ bucket: b, items: choices.filter(c => c.bucket === b) })).filter(g => g.items.length > 0);
  }, [choices]);

  const current = choices.find(x => x.key === selected);
  const needsReason = laborOn && current?.type?.requires_reason === 1;
  const reason = reasonPreset && reasonPreset !== 'その他（下に記入）' ? (reasonText ? `${reasonPreset}：${reasonText}` : reasonPreset) : reasonText;

  const workMin = useMemo(() => {
    if (!start || !end) return null;
    const [sh = 0, sm = 0] = start.split(':').map(Number);
    const [eh = 0, em = 0] = end.split(':').map(Number);
    let m = (eh * 60 + em) - (sh * 60 + sm);
    if (m < 0) m += 1440; // 日をまたぐ作業（22:00〜02:00 など）
    return m;
  }, [start, end]);

  /** 終了時刻が開始より前なら翌日の日付にする（夜間の作業） */
  const endIso = () => {
    const [sh = 0, sm = 0] = start.split(':').map(Number);
    const [eh = 0, em = 0] = end.split(':').map(Number);
    const crosses = (eh * 60 + em) <= (sh * 60 + sm);
    const d = new Date(`${date}T00:00:00+09:00`);
    if (crosses) d.setDate(d.getDate() + 1);
    const y = d.getFullYear(), mo = String(d.getMonth() + 1).padStart(2, '0'), da = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${da}T${end}:00+09:00`;
  };

  const save = async () => {
    const c = current;
    if (!c) { setError('作業内容を選んでください'); return; }
    if (!start || !end) { setError('開始と終了の時刻を入れてください'); return; }
    if ((workMin ?? 0) <= 0) { setError('終了時刻は開始時刻より後にしてください'); return; }
    if (needsReason && !reason.trim()) { setError(`「${c.name}」は事由の入力が必要です`); return; }
    setSaving(true); setError('');
    try {
      if (c.kind === 'session') {
        // 出退勤を伴う業務として記録する
        await apiFetch('/attendance', {
          method: 'POST',
          body: JSON.stringify({ work_date: date, clock_in: start, clock_out: end, work_type: c.name, vessel_id: vesselId, reason: note || '業務を追加' }),
        });
      } else {
        // 時間を指定して行った作業として記録する（入れ物のセッションを確保してから積む）
        const sess = await apiFetch<{ data: { id: string } }>('/activity-logs/session', {
          method: 'POST',
          body: JSON.stringify({ target_date: date, vessel_id: vesselId }),
        });
        await apiFetch('/activity-logs', {
          method: 'POST',
          body: JSON.stringify({
            work_session_id: sess.data.id, vessel_id: vesselId, activity_master_id: c.masterId,
            started_at: `${date}T${start}:00+09:00`, ended_at: endIso(),
            note: note || undefined,
            ...(laborOn && c.type?.labor_category ? { labor_category: c.type.labor_category } : {}),
            ...(needsReason ? { reason: reason.trim() } : {}),
          }),
        });
      }
      onSaved();
    } catch (e: any) { setError(e?.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const renderChoice = (c: Choice) => {
    const on = selected === c.key;
    const accent = c.bucket === 'exempt' ? '#dc2626' : c.bucket === 'nonwork' ? '#64748b' : '#0057d9';
    return (
      <button key={c.key} onClick={() => { setSelected(c.key); setError(''); }}
        style={{
          padding: '14px 8px', borderRadius: 12, cursor: 'pointer', minHeight: 76,
          border: on ? `2.5px solid ${accent}` : '1.5px solid #e2e8f0',
          background: on ? accent : '#f8fafc',
          color: on ? '#fff' : '#1e293b',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5,
        }}>
        <span style={{ fontSize: 22, lineHeight: 1 }}>{c.emoji}</span>
        <span style={{ fontSize: 14, fontWeight: 800, textAlign: 'center', lineHeight: 1.25 }}>{c.name}</span>
      </button>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, maxHeight: '88dvh', overflowY: 'auto', boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 19, fontWeight: 900, color: '#0f172a' }}>業務を追加</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{date}</div>
          </div>
          <button onClick={onClose} aria-label="閉じる"
            style={{ width: 38, height: 38, border: 'none', background: '#f1f5f9', borderRadius: '50%', cursor: 'pointer', fontSize: 17, color: '#64748b' }}>✕</button>
        </div>

        {/* 作業内容 */}
        <div style={{ fontSize: 14, fontWeight: 800, color: '#334155', marginBottom: 8 }}>何をしましたか？</div>
        {choices.length === 0 ? (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 12, fontSize: 13, color: '#92400e', marginBottom: 14 }}>
            この船に業務内容が登録されていません。管理者にご連絡ください。
          </div>
        ) : laborOn && groups.length > 1 ? (
          groups.map(g => (
            <div key={g.bucket} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: BUCKET_LABEL[g.bucket].color }}>{BUCKET_LABEL[g.bucket].title}</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{BUCKET_LABEL[g.bucket].note}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>{g.items.map(renderChoice)}</div>
            </div>
          ))
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>{choices.map(renderChoice)}</div>
        )}

        {/* 選んだ作業の扱い */}
        {laborOn && current && current.bucket !== 'work' && (
          <div style={{ background: current.bucket === 'exempt' ? '#fef2f2' : '#f8fafc', border: `1px solid ${current.bucket === 'exempt' ? '#fecaca' : '#e2e8f0'}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
            <b style={{ color: current.bucket === 'exempt' ? '#b91c1c' : '#334155' }}>{current.type?.labor_category_name ?? current.name}</b>
            {current.type?.counts_as_work === 0 && '：労働時間には含めません。'}
            {current.type?.counts_toward_limit === 0 && '：労働時間に含めますが、上限計算からは除外されます。'}
            {current.type?.permits_over_limit === 1 && '：労働時間に含め、上限を超えても合法（割増）として扱います。'}
            {current.type?.notify_shore === 1 && ' 記録すると陸上へ通知されます。'}
          </div>
        )}

        {/* 事由（区分が求める場合） */}
        {needsReason && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#b91c1c', marginBottom: 6 }}>事由（必須）</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {REASON_PRESETS.map(p => (
                <button key={p} onClick={() => setReasonPreset(p)}
                  style={{ padding: '7px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: reasonPreset === p ? '2px solid #dc2626' : '1px solid #e2e8f0', background: reasonPreset === p ? '#fef2f2' : '#fff', color: reasonPreset === p ? '#b91c1c' : '#334155' }}>
                  {p}
                </button>
              ))}
            </div>
            <input value={reasonText} onChange={e => setReasonText(e.target.value)} placeholder="状況を一言（例：北東の風18m/s、○○港沖で錨泊待機）"
              style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, background: '#f8fafc' }} />
          </div>
        )}

        {/* 時刻 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#334155' }}>はじめた時刻</span>
            <input type="time" value={start} onChange={e => setStart(e.target.value)}
              style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '12px', fontSize: 17, fontWeight: 700, background: '#f8fafc' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#334155' }}>おわった時刻</span>
            <input type="time" value={end} onChange={e => setEnd(e.target.value)}
              style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '12px', fontSize: 17, fontWeight: 700, background: '#f8fafc' }} />
          </label>
        </div>
        {workMin !== null && workMin > 0 && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 14, fontWeight: 700, color: '#166534' }}>
            {Math.floor(workMin / 60)}時間{workMin % 60 > 0 ? `${workMin % 60}分` : ''}
            {current?.kind === 'activity' && (() => { const [sh = 0, sm = 0] = start.split(':').map(Number); const [eh = 0, em = 0] = end.split(':').map(Number); return (eh * 60 + em) <= (sh * 60 + sm); })() && <span style={{ fontSize: 12, marginLeft: 8, color: '#15803d' }}>（翌日 {end} まで）</span>}
          </div>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#334155' }}>備考（任意）</span>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="必要なときだけ入力してください"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, resize: 'none', fontFamily: 'inherit', background: '#f8fafc' }} />
        </label>

        {error && <div style={{ color: '#dc2626', fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: 15, border: '1.5px solid #e2e8f0', borderRadius: 12, background: '#fff', cursor: 'pointer', fontSize: 15, color: '#475569', fontWeight: 800 }}>
            やめる
          </button>
          <button onClick={save} disabled={saving}
            style={{ flex: 2, padding: 15, border: 'none', borderRadius: 12, background: saving ? '#93c5fd' : '#0057d9', color: '#fff', fontWeight: 900, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 15 }}>
            {saving ? '保存中...' : '追加する'}
          </button>
        </div>
      </div>
    </div>
  );
};
